import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CommonResponse } from '../../models';
import { environment } from '../../../../environments/environment';
import { ProfileUserUniversitySchoolUniversity } from '../../models/profile/profile';
import { DidacticProgram } from '../../models/didactic-program/didactic-program';
import { Career } from '../../models/career/career';
import { LogBook } from '../../models/registration/log-book';
import { map } from 'rxjs/operators';
import { Evaluation } from '../../models/evaluation/evaluation';
import { File } from '../../models/file';

@Injectable({
	providedIn: 'root',
})
export class TutorCareerService {
	constructor(private _http: HttpClient) {}

	createTutorCareer(data: FormData | any): Observable<CommonResponse<boolean>> {
		return this._http.post<CommonResponse<boolean>>(
			`${environment.apiUniversityUrl}/careers_profiles_users_universities_schools_universities`,
			data
		);
	}

	showTutorCareer(id: string): Observable<CommonResponse<ProfileUserUniversitySchoolUniversity>> {
		return this._http.get<CommonResponse<ProfileUserUniversitySchoolUniversity>>(
			`${environment.apiUniversityUrl}/careers_profiles_users_universities_schools_universities/${id}`
		);
	}

	updateTutorCareer(id: string, data: FormData | any): Observable<CommonResponse<any>> {
		return this._http.post<CommonResponse<any>>(
			`${environment.apiUniversityUrl}/careers_profiles_users_universities_schools_universities/${id}`,
			data
		);
	}

	deleteTutorCareer(id: string): Observable<CommonResponse<boolean>> {
		return this._http.delete<CommonResponse<boolean>>(
			`${environment.apiUniversityUrl}/careers_profiles_users_universities_schools_universities/${id}`
		);
	}

	deleteAttachment(id: string): Observable<CommonResponse<boolean>> {
		return this._http.delete<CommonResponse<boolean>>(
			`${environment.apiUniversityUrl}/careers_profiles_users_universities_schools_universities/delete-attachment/${id}`
		);
	}

	getTutorBySchoolUniversity(id: string): Observable<CommonResponse<Array<ProfileUserUniversitySchoolUniversity>>> {
		return this._http.get<CommonResponse<Array<ProfileUserUniversitySchoolUniversity>>>(
			`${environment.apiUniversityUrl}/profiles_users_universities_schools_universities/tutors/filtered_by/school_university/${id}`
		);
	}

	/////////////////////
	getTraineesFilteredByTutor(): Observable<CommonResponse<Array<ProfileUserUniversitySchoolUniversity>>> {
		return this._http.get<CommonResponse<Array<ProfileUserUniversitySchoolUniversity>>>(
			`${environment.apiSchoolUrl}/profiles_users_universities_schools_universities/trainees/filtered_by/auth_tutor`
		);
	}

	getDashboardStats(): Observable<
		CommonResponse<{
			log_book_stats: Array<{ status: string; status_count: number }>;
			cfu_stats: Array<{ cfu_type: string; total_cfu: number }>;
			school: { id: string; name: string } | null;
			trainee_count: number;
		}>
	> {
		return this._http.get<CommonResponse<any>>(
			`${environment.apiSchoolUrl}/profiles_users_universities_schools_universities/trainees/dashboard_stats/filtered_by/auth_tutor`
		);
	}

	getShowCareerByTutor(): Observable<CommonResponse<Career>> {
		return this._http.get<CommonResponse<Career>>(`${environment.apiSchoolUrl}/career`);
	}

	getLastTenRegistration(): Observable<CommonResponse<Array<LogBook>>> {
		return this._http.get<CommonResponse<Array<LogBook>>>(`${environment.apiSchoolUrl}/career/tutor/log_books/last_ten`).pipe(
			map(response => {
				if (response.data) {
					response.data.forEach(logBook => {
						const totQuantity = logBook.log_book_details.reduce((acc, item) => acc + item.quantity, 0);
						const totMinutes = logBook.log_book_details.reduce((acc, item) => acc + item.minutes, 0);
						logBook.tot_quantity = totQuantity;
						logBook.tot_minutes = totMinutes;
					});
				}
				return response;
			})
		);
	}

	getResgistrationsList(): Observable<CommonResponse<Array<LogBook>>> {
		return this._http.get<CommonResponse<Array<LogBook>>>(`${environment.apiSchoolUrl}/career/tutor/log_books`).pipe(
			map(response => {
				if (response.data) {
					response.data.forEach(logBook => {
						const totQuantity = logBook.log_book_details.reduce((acc, item) => acc + item.quantity, 0);
						const totMinutes = logBook.log_book_details.reduce((acc, item) => acc + item.minutes, 0);
						logBook.tot_quantity = totQuantity;
						logBook.tot_minutes = totMinutes;
					});
				}
				return response;
			})
		);
	}

	getRegistrationFilteredByYear(year: string): Observable<CommonResponse<Array<LogBook>>> {
		return this._http
			.get<CommonResponse<Array<LogBook>>>(`${environment.apiSchoolUrl}/career/tutor/log_books/filtered_by/year/${year}`)
			.pipe(
				map(response => {
					if (response.data) {
						response.data.forEach(logBook => {
							const totQuantity = logBook.log_book_details.reduce((acc, item) => acc + item.quantity, 0);
							const totMinutes = logBook.log_book_details.reduce((acc, item) => acc + item.minutes, 0);
							logBook.tot_quantity = totQuantity;
							logBook.tot_minutes = totMinutes;
						});
					}
					return response;
				})
			);
	}

	confirmRegistrations(data: { status: string; log_book_detail_ids: Array<string>; note?: string }): Observable<CommonResponse<boolean>> {
		return this._http.patch<CommonResponse<boolean>>(`${environment.apiSchoolUrl}/career/tutor/log_book_details`, data);
	}

	tutorEvaluations(data: FormData): Observable<CommonResponse<Evaluation>> {
		return this._http.post<CommonResponse<Evaluation>>(`${environment.apiSchoolUrl}/career/tutor/evaluations`, data);
	}

	editTutorEvaluation(id: string, data: FormData): Observable<CommonResponse<Evaluation>> {
		data.append('_method', 'PUT');
		return this._http.post<CommonResponse<Evaluation>>(`${environment.apiSchoolUrl}/career/tutor/evaluations/${id}`, data);
	}

	getSkillTotalizersForLogBookDetails(
		log_book_detail_ids: Array<string>
	): Observable<CommonResponse<{ totalizers: Array<any>; career_id: string }>> {
		return this._http.post<CommonResponse<{ totalizers: Array<any>; career_id: string }>>(
			`${environment.apiSchoolUrl}/career/log_book_details/totalizers`,
			{
				log_book_detail_ids,
			}
		);
	}

	getSkillTotalizersByYear(year: number): Observable<CommonResponse<{ totalizers: Array<any>; career_id: string }>> {
		return this._http.get<CommonResponse<{ totalizers: Array<any>; career_id: string }>>(
			`${environment.apiSchoolUrl}/career/log_book_details/skill_totalizers/year/${year}`
		);
	}

	assignSkillTotalizersByYear(year: number, data: { totalizer_ids: Array<string> }): Observable<CommonResponse<boolean>> {
		return this._http.post<CommonResponse<boolean>>(
			`${environment.apiSchoolUrl}/career/log_book_details/skill_totalizers/year/${year}/assign`,
			data
		);
	}

	getSkillTotalizersByShift(shift_id: string): Observable<CommonResponse<{ totalizers: Array<any>; career_id: string }>> {
		return this._http.get<CommonResponse<{ totalizers: Array<any>; career_id: string }>>(
			`${environment.apiSchoolUrl}/career/shifts/${shift_id}/skill_totalizers`
		);
	}

	assignSkillTotalizersByShift(shift_id: string, data: { totalizer_ids: Array<string> }): Observable<CommonResponse<boolean>> {
		return this._http.post<CommonResponse<boolean>>(`${environment.apiSchoolUrl}/career/shifts/${shift_id}/skill_totalizers/assign`, data);
	}
}
